/** biome-ignore-all lint/a11y/useMediaCaption: raw ad creatives, no captions exist */
"use client";

/**
 * Full-size video viewer. Thumbnails around the portal autoplay muted;
 * pressing one opens this with sound.
 *
 * TWO SHAPES, AND TWO PLAYERS — which is the part worth explaining, because
 * everywhere else in this codebase the answer is one component at both
 * widths.
 *
 * Past rail width it is a dialog with the design system's `VideoPlayer`:
 * press the frame to play, mute in the corner, and a frame measured from the
 * file's own metadata so a 9:16 phone video is not letterboxed into a
 * landscape box. Native `controls` there are a browser chrome bar pinned
 * across the bottom of the creative, in a different visual language on every
 * platform, over the thing being reviewed.
 *
 * On a phone it is a bottom sheet with the *native* player, deliberately. A
 * custom control bar on a touch screen gives up what the platform does far
 * better than we will: the scrubber's touch target and its thumbnail
 * preview, AirPlay, picture-in-picture, the fullscreen gesture, the lock
 * screen and Control Center handoff, and the system captions toggle. The
 * platform's player is the better experience, so it wins, and the custom
 * chrome exists for the case a pointer is driving.
 */

import { type ReactNode, useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
} from "@/components/ui/design-system/modal";
import { Sheet } from "@/components/ui/design-system/sheet";
import { Hint } from "@/components/ui/design-system/text";
import { useIsWide } from "@/components/ui/design-system/use-is-wide";
import {
  fillsNineBySixteen,
  VideoPlayer,
} from "@/components/ui/design-system/video-player";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { cn } from "@/lib/utils";
import { Money } from "./one/ui";

export type LightboxVideo = {
  url: string;
  /** What this is. Always the name, never the number. */
  title: string;
  /**
   * A headline figure, when the viewer opened this to see one.
   *
   * Separate from `description` because the two want opposite treatments and
   * the header could not tell them apart: the top-ads row was passing "Top ad
   * #3" as the title and "Est. payout $20,000" as the supporting line, so the
   * rank — a label — was the biggest thing on the header and the money was
   * grey 13px underneath it. Which of the two matters is the caller's to say,
   * not something a shared header can infer from a string.
   */
  value?: string;
  valueLabel?: string;
  /** A supporting line, for a caller with no figure to show. */
  description?: string;
  posterUrl?: string | null;
};

export function VideoLightbox({
  video,
  onClose,
  className,
  depthRootId,
  rail,
}: {
  video: LightboxVideo | null;
  onClose: () => void;
  /**
   * The layer the panel should wear. `/one` passes `PORTAL_OVERLAY`, because
   * a portalled panel lands outside the route's `ds-next` div; the staff
   * pages that share this viewer pass nothing and keep Framework's colours,
   * which are correct there.
   */
  className?: string;
  /** Sheet only: the element that recedes behind it. */
  depthRootId?: string;
  /**
   * Desk only: the rest of a set, beside the video. With it the panel goes
   * two-column at the DS `xl` width, the words and the rail on the right;
   * without it the panel stays as wide as the video, because a text column
   * with two lines in it is margin wearing a different name. The phone sheet
   * ignores it: there the shelf behind the sheet is the set.
   */
  rail?: ReactNode;
}) {
  // The last video is kept so the panel stays mounted through the close
  // animation instead of pop-unmounting the instant the parent clears its
  // selection.
  const t = useCreatorTranslations("videos");
  const [lastVideo, setLastVideo] = useState<LightboxVideo | null>(null);
  const nativeRef = useRef<HTMLVideoElement | null>(null);
  const wide = useIsWide();
  // True for the frame after the open panel is handed a different file (a
  // rail pick), so the new video settles in from a soft blur instead of
  // hard-cutting through a black frame while its metadata loads. Not on the
  // first open: the panel's own entrance covers that.
  const [settling, setSettling] = useState(false);
  const shownUrlRef = useRef<string | null>(null);
  useEffect(() => {
    const url = video?.url ?? null;
    const previous = shownUrlRef.current;
    shownUrlRef.current = url;
    if (!url || !previous || url === previous) return;
    setSettling(true);
    const id = window.setTimeout(() => setSettling(false), 30);
    return () => window.clearTimeout(id);
  }, [video?.url]);

  // Playback starts here — once per opening — and ONLY here. `video` is
  // parent state, so its identity is stable across unrelated re-renders;
  // playing from a ref callback would re-fire whenever that callback's
  // identity changed and silently un-pause a video the viewer had paused.
  // Running just after the opening click's commit keeps us inside the
  // transient user activation browsers require for unmuted playback; if one
  // blocks it anyway, the visible controls are the fallback.
  useEffect(() => {
    if (video) {
      setLastVideo(video);
      void nativeRef.current?.play().catch(() => {});
    }
  }, [video]);

  // The file's own shape, keyed to its URL so a rail pick never inherits the
  // previous video's measurement.
  const [measured, setMeasured] = useState<{
    url: string;
    ratio: number;
  } | null>(null);

  const shown = video ?? lastVideo;
  if (!shown) return null;

  /*
    The frame stays 9:16 so the panel doesn't reshape as files load, and it is
    filled only when the file really is 9:16: those are a few pixels off the
    ratio and bars would cost more than the sliver the fill crops. Anything
    else (a 4:5 or square ad) is fitted whole and letterboxed, because filling
    a 9:16 frame with it crops its sides, captions included. Until the file
    reports its size, assume the common case.
  */
  const ratio = measured?.url === shown.url ? measured.ratio : null;
  const fit = ratio === null || fillsNineBySixteen(ratio) ? "cover" : "contain";
  const measure = (url: string, width: number, height: number) => {
    if (width <= 0 || height <= 0) return;
    const next = width / height;
    setMeasured((current) =>
      current?.url === url && current.ratio === next
        ? current
        : { url, ratio: next },
    );
  };

  const open = video !== null;
  const close = () => {
    // Stop the audio now: the element stays mounted, and would keep playing,
    // through the exit animation.
    nativeRef.current?.pause();
    onClose();
  };

  /*
    Name, then the number — and the number is the bigger of the two.

    It is the `Money` block the rest of the portal uses for a figure with its
    name over it, so the modal agrees with Home rather than inventing a third
    way to print an amount. The label sits above the figure, which is the DS
    rule for this pair: de-emphasis is colour, and the thing being compared
    is the one carried at figure size.
  */
  const figure = shown.value ? (
    <Money
      label={shown.valueLabel ?? t("lightbox.value")}
      value={shown.value}
    />
  ) : shown.description ? (
    <Hint muted>{shown.description}</Hint>
  ) : null;

  if (!wide) {
    return (
      <Sheet
        open={open}
        onClose={close}
        title={shown.title}
        className={className}
        depthRootId={depthRootId}
      >
        <div className="flex flex-col gap-nf-ds-item pt-nf-ds-1">
          {/* The sheet header carries the name as its own title, so only the
              figure repeats here — a name printed twice in 60 points of each
              other is the header saying it and then saying it again. */}
          {figure}
          {/*
            An exact 9:16 box, sized from its height.

            `aspect-[9/16] w-full` with a `max-height` does not stay 9:16 —
            the cap shortens the box while the width holds, so the frame
            comes out wider than the ratio it declares. Driving it from the
            height and letting the width follow keeps the ratio exact at
            every viewport, and `max-w-full` hands the constraint back to
            the width on a screen too narrow for it.
          */}
          <div className="mx-auto aspect-[9/16] h-[60vh] max-w-full overflow-hidden rounded-nf-ds-card bg-black">
            <video
              ref={nativeRef}
              src={shown.url}
              poster={shown.posterUrl ?? undefined}
              onLoadedMetadata={(e) =>
                measure(
                  shown.url,
                  e.currentTarget.videoWidth,
                  e.currentTarget.videoHeight,
                )
              }
              className={cn(
                "size-full",
                fit === "cover" ? "object-cover" : "object-contain",
              )}
              controls
              playsInline
            />
          </div>
        </div>
      </Sheet>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <ModalContent
        size={rail ? "xl" : "default"}
        className={cn(rail ? "max-w-4xl" : "max-w-[440px]", className)}
        // Radix warns when the content has neither a description nor an
        // explicit opt-out.
        aria-describedby={undefined}
      >
        {/*
          Two columns when there is a rail: the video at its height on the
          left, the words and the rest of the set on the right. A 9:16 frame
          capped by the viewport is about 300 points wide, so the width here
          is spent on the set, never on margin around the frame; a lone video
          keeps the narrow stacked panel.
        */}
        <div
          className={cn(
            rail &&
              "grid grid-cols-[auto_minmax(0,1fr)] items-start gap-nf-ds-8",
          )}
        >
          {/* The name stays the dialog's accessible name on its own; folding
              the figure into it would have a screen reader announce "Top ad
              #3 Estimated payout $20,000" as the title of the window. */}
          {rail ? null : (
            <ModalHeader className="gap-nf-ds-item">
              <ModalTitle>{shown.title}</ModalTitle>
              {figure}
            </ModalHeader>
          )}
          {/*
            An exact 9:16 box sized from the height, because a `max-height`
            on a full-width frame shortens the box while the width holds and
            quietly stops being the ratio it claims. `lockAspectRatio` so the
            player does not re-shape the frame to the file; `fit` as decided
            above, so only a true 9:16 file is filled.
          */}
          {/*
            The blur is Emil's crossfade mask: two videos swapping read as two
            objects, one blurring into focus reads as one surface changing.
            `blur-xs` is the scale's smallest step (4px), which stays well
            under the point where a blur reads as a smear. 200ms `ease`
            rather than the house ease-out, because this is a settle, not an
            entrance. Reduced motion keeps the opacity step and drops the
            blur.
          */}
          <VideoPlayer
            src={shown.url}
            poster={shown.posterUrl}
            aspectRatio="9:16"
            lockAspectRatio
            fit={fit}
            onAspectRatio={(_, size) =>
              measure(shown.url, size.width, size.height)
            }
            showScrubber
            // The settle rides on the player itself, not a wrapper: a
            // `w-fit` wrapper took away the panel the player's `max-w-full`
            // shrinks against, and the narrow panel overflowed sideways.
            className={cn(
              "h-[70vh] w-auto max-w-full transition-[opacity,filter] duration-200 ease-[ease]",
              !rail && "mx-auto",
              settling && "opacity-70 blur-xs motion-reduce:blur-none",
            )}
          />
          {rail ? (
            <div className="flex min-h-0 flex-col gap-nf-ds-5 self-stretch pr-nf-ds-8">
              <ModalHeader className="gap-nf-ds-item">
                <ModalTitle>{shown.title}</ModalTitle>
                {figure}
              </ModalHeader>
              {/* Capped at the video's height, so a long set scrolls beside
                  the frame rather than stretching the panel past it. */}
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                {rail}
              </div>
            </div>
          ) : null}
        </div>
      </ModalContent>
    </Modal>
  );
}
